<template>
  <div id="admin-users-register">
    <Title title="Usuarios"
           subtitle="Creación de usuarios y asociación de conductores"/>
    <el-row :gutter="10"
            v-if="emailCreated === null">
      <el-col>
        <step1 :accountTypes="accountTypes"
               @selected="firstStep"/>
      </el-col>
      <el-col v-if="step === 2">
        <step2 :identity=identity
               :accountTypeId="accountTypeId"
               @send="send"
               @cancel="goToRegister"/>
      </el-col>
      <el-col v-if="step === 3">
        <step3 :identity=identity
               :accountTypeId="accountTypeId"
               @send="send"
               @cancel="goToRegister"/>
      </el-col>
    </el-row>
    <el-row v-else>
      <SubTitle align="center"
                text="¡ creación exitosa !"/>
      <div class="card-section">
        <p class="title">USUARIO</p>
        <p class="order">{{emailCreated}}</p>
      </div>
      <div align="center">
        <p class="continue">¿Qué deseas hacer?:</p>
        <el-button class="flat"
                   @click="goToRegister">CREAR NUEVO USUARIO</el-button>
        <el-button class="default"
                   @click="goToUserView">VER USUARIOS</el-button>
      </div>
    </el-row>
  </div>
</template>

<script>
import { apiRequest } from '@/backend/vue-axios/axios';
import { startRumTiming, getRumDuration, trackBusinessFlow } from '@/observability/datadogRum';
import { tenantIdentity } from '@/mixins/tenants';
import Title from '../../components/base/Title.vue';
import Step1 from '../../components/user/Step1.vue';
import Step2 from '../../components/user/Step2.vue';
import Step3 from '../../components/user/Step3.vue';
import SubTitle from '../../components/base/SubTitle.vue';

export default {
  name: 'UserRegister',
  components: {
    Title,
    SubTitle,
    Step1,
    Step2,
    Step3,
  },
  data() {
    return {
      step: 1, // pasos
      accountTypes: [], // lista de accounts
      accountTypeId: 0,
      identity: {}, // config por tenant
      emailCreated: null,
    };
  },
  mounted() {
    this.getTypes();
    this.identity = tenantIdentity();
  },
  methods: {
    // Step 1: Seleccionar tipo de usuario
    // Step 2: Formulario de usuario normal
    // Step 3: Formulario de usuario driver (validador)
    firstStep(typeId) {
      this.accountTypeId = typeId;
      this.step = typeId === 4 ? 3 : 2;
    },
    send(params) {
      const startedAt = startRumTiming();
      const service = (params.account_types_id === 7 || params.account_types_id === 8)
        ? 'register_inhouse_tren_logistico'
        : 'registerV2';
      const enterpriseTrackingValue = Array.isArray(params.enterprise_ids)
        ? params.enterprise_ids.join(',')
        : (params.enterprise_id || 'none');

      apiRequest({
        module: 'crud',
        service,
        params,
      }).then((response) => {
        this.emailCreated = response && response.data && response.data.account
          ? response.data.account.email
          : params.email;
        trackBusinessFlow({
          module: 'users',
          flow: 'user_management',
          action: 'create_user',
          status: 'success',
          durationMs: getRumDuration(startedAt),
          context: {
            target_user_email: params.email || 'unknown',
            target_user_name: [params.name, params.last_name].filter(Boolean).join(' ') || 'unknown',
            target_account_type: params.account_types_id || 'unknown',
            target_enterprise_id: enterpriseTrackingValue,
          },
        });
        this.$notify({
          title: 'Completado',
          message: 'Usuario creado con éxito',
          type: 'success',
        });
        // this.clean();
      }).catch((error) => {
        const isInhouseTrenLogistico = params.account_types_id === 7 || params.account_types_id === 8;
        const status = error && error.response ? error.response.status : null;
        let message = error.response && error.response.data
          ? error.response.data.message
          : 'No se pudo crear el usuario';

        if (isInhouseTrenLogistico && status === 400) {
          message = 'Datos inválidos. Revisa tipo de cuenta y empresas seleccionadas.';
        } else if (isInhouseTrenLogistico && status === 404) {
          message = 'Una o más empresas seleccionadas no existen.';
        } else if (isInhouseTrenLogistico && status === 500) {
          message = 'Error interno al crear el usuario. Intenta nuevamente.';
        }

        trackBusinessFlow({
          module: 'users',
          flow: 'user_management',
          action: 'create_user',
          status: 'error',
          durationMs: getRumDuration(startedAt),
          context: {
            target_user_email: params.email || 'unknown',
            target_user_name: [params.name, params.last_name].filter(Boolean).join(' ') || 'unknown',
            target_account_type: params.account_types_id || 'unknown',
            target_enterprise_id: enterpriseTrackingValue,
            error_message: error.response && error.response.data
              ? error.response.data.message
              : 'unknown_error',
          },
        });
        this.$notify({
          type: 'error',
          title: 'Error en la creación',
          message,
        });
        // this.clean();
      });
    },
    goToRegister(r) {
      this.$router.go(r);
    },
    goToUserView() {
      this.$router.push('userList');
    },
    getTypes() {
      apiRequest({
        module: 'crud',
        service: 'get_account_types_v2',
        params: { limit: 1000 },
      }).then((response) => {
        response.data.forEach((val) => {
          if (val.type === 'JefeReparto') {
            // eslint-disable-next-line no-param-reassign
            val.type = 'Jefe de Reparto';
          } else if (val.type === 'SupervisorTransporte') {
            // eslint-disable-next-line no-param-reassign
            val.type = 'Supervisor de Transporte';
          }
          this.accountTypes.push(val);
        });
      }).catch(() => {
        this.$notify.error({
          title: 'Error',
          message: 'Error al cargar datos',
        });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.card-section {
  padding: 0 17%;
  width: fit-content;
  margin: 0 auto 20px auto;
  border: 1px solid #E0E0E0;
  .title {
    color: #616161;
    font-weight: 900;
    margin: 32px 0 0 0;
  }
  .order {
    color: #AAD500;
    font-size: 40px;
    font-weight: 300;
    margin: 0 0 20px 0;
  }
}
</style>
